import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { ProductIndexItemInterface } from "../../common/interfaces/apiV2/shared/ProductIndexItemInterface";
import { SelectedFilterInterface } from "../../common/interfaces/productRange/SelectedFilterInterface";
import { ProductTypeEnum } from "../../common/enums/ProductTypeEnum";

interface ProductRangePageInterface {
    gridLayout: boolean;
    mobileFiltersActive: boolean;
    productType: ProductTypeEnum[];
    selectedProducts: ProductIndexItemInterface[];
    selectedFilters: Record<number, SelectedFilterInterface>;
    openedCategoryIds: number[];
    disableCheckbox?: boolean;
}

const initialState: ProductRangePageInterface = {
    gridLayout: true,
    mobileFiltersActive: false,
    productType: [ProductTypeEnum.products],
    selectedProducts: [],
    selectedFilters: {},
    openedCategoryIds: [],
    disableCheckbox: false,
};

const productRangeSlice = createSlice({
    name: "productRange",
    initialState,
    reducers: {
        setGridLayout: (state, action: PayloadAction<boolean>) => {
            state.gridLayout = action.payload;
        },
        setProductType: (state, action: PayloadAction<ProductTypeEnum>) => {
            state.productType = [action.payload];
            state.openedCategoryIds = [];
        },
        openCategory: (state, action: PayloadAction<number>) => {
            if (!state.openedCategoryIds.includes(action.payload)) {
                state.openedCategoryIds.push(action.payload);
            }
        },
        clearOpenedCategories: (state) => {
            state.openedCategoryIds = [];
        },
        setMobileFiltersActive: (state, action: PayloadAction<boolean>) => {
            state.mobileFiltersActive = action.payload;
            if (state.mobileFiltersActive) {
                document.body.style.overflow = "hidden";
            }
            if (!state.mobileFiltersActive) {
                document.body.style.overflow = "auto";
            }
        },
        selectProduct: (
            state,
            action: PayloadAction<ProductIndexItemInterface>,
        ) => {
            const isSelected = current(state.selectedProducts).some(
                (product) => product.id === action.payload.id,
            );

            if (isSelected) {
                state.disableCheckbox = false;
                state.selectedProducts = current(state.selectedProducts).filter(
                    (product) => product.id !== action.payload.id,
                );
                return;
            }

            state.disableCheckbox = true;
            if (state.selectedProducts.length < 50) {
                state.selectedProducts.push(action.payload);
                state.disableCheckbox = state.selectedProducts.length === 50;
            }
        },
        clearSelectedProducts: (state) => {
            state.selectedProducts = [];
        },
        selectFilter: (
            state,
            action: PayloadAction<{
                filter: { id: number };
                option: { id: string };
            }>,
        ) => {
            // Keep openedCategoryIds so open categories refetch with the new filter.
            const filterId = action.payload.filter.id;
            const optionId = action.payload.option.id;

            if (!state.selectedFilters[filterId]) {
                state.selectedFilters[filterId] = {
                    id: filterId,
                    options: [optionId],
                };
                return;
            }

            const hasOption = state.selectedFilters[filterId].options.some(
                (option) => option === optionId,
            );

            if (hasOption) {
                const remainingOptions = state.selectedFilters[
                    filterId
                ].options.filter((option) => option !== optionId);

                if (remainingOptions.length === 0) {
                    delete state.selectedFilters[filterId];
                    return;
                }

                state.selectedFilters[filterId] = {
                    id: filterId,
                    options: remainingOptions,
                };
                return;
            }

            state.selectedFilters[filterId] = {
                id: filterId,
                options: [...state.selectedFilters[filterId].options, optionId],
            };
        },
        resetFilter: (state) => {
            state.selectedFilters = [];
            state.openedCategoryIds = [];
        },
    },
});

export const {
    selectProduct,
    clearSelectedProducts,
    selectFilter,
    setGridLayout,
    setProductType,
    setMobileFiltersActive,
    resetFilter,
    openCategory,
    clearOpenedCategories,
} = productRangeSlice.actions;
export const productRangeReducer = productRangeSlice.reducer;
